import { createContext, ReactNode, useContext, useState, useEffect, useCallback } from 'react';
import { authService, TOKEN_KEY, USER_KEY, type ApiError } from 'modules/auth/services/authService';
import { concluirReautenticacao, ouvirReautenticacao } from 'modules/auth/services/reautenticacao';
import {
  ANTECEDENCIA_RENOVACAO_MS,
  INTERVALO_NOVA_TENTATIVA_MS,
  desmarcarSessaoViaEntra,
  expiracaoDoToken,
  marcarSessaoViaEntra,
  podeRenovarPeloEntra,
  precisaRenovar,
  reentrarComSenha as retomarComSenha,
  renovarSessao,
  renovarSessaoComPopup,
  retomarSessaoPeloEntra,
  tokenVencido,
  type SessaoRenovada
} from 'modules/auth/services/renovacaoSessao';
import type { AuthSessao, LoginResponse } from 'types/domain';

/** O usuário da sessão — ver `AuthSessao` em `types/domain`: o login traz menos que o `/me`. */
type AuthUser = AuthSessao;
type LoginResult = LoginResponse;

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginEntra: (idToken: string) => Promise<LoginResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  permissions: string[];
  hasPermission: (perm: string) => boolean;
  /** Quando o JWT vence sem ter conseguido renovar sozinho (Safari): o aviso de expiração mostra. */
  sessaoExpiraEm: number | null;
  /** Renova pelo popup do Entra, sem recarregar a página. Tem de ser chamado no clique. */
  continuarConectado: () => Promise<void>;
  /** A sessão acabou no meio do uso e não renovou sozinha: o diálogo pede para entrar de novo. */
  sessaoEncerrada: boolean;
  /** Entra de novo com a senha, pelo diálogo de sessão encerrada (quem usa login local). */
  reentrarComSenha: (senha: string) => Promise<void>;
}

type ResultadoDoMe = 'ok' | 'recusado' | 'removido' | 'indisponivel';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(authService.getStoredUser() as AuthUser | null);
  const [token, setToken] = useState<string | null>(authService.getToken());
  const [loading, setLoading] = useState(true);
  const [sessaoExpiraEm, setSessaoExpiraEm] = useState<number | null>(null);
  const [sessaoEncerrada, setSessaoEncerrada] = useState(false);

  const isAuthenticated = !!token && !!user;

  // O diálogo de sessão encerrada abre e fecha com o portão de reautenticação.
  useEffect(() => ouvirReautenticacao(setSessaoEncerrada), []);

  // Cookie de arquivo (imagem inline do chamado, T-484): acompanha o token. Cada token novo — carga
  // do app, login, SSO, renovação, outra aba — reemite o cookie com a validade dele. O logout apaga
  // (authService.logout).
  useEffect(() => {
    if (token) void authService.emitirCookieDeArquivo();
  }, [token]);

  // Sessão renovada ou retomada (silenciosa, popup ou senha): estado novo, e as chamadas que
  // esperavam no diálogo de sessão encerrada são repetidas com o token novo.
  const aplicarSessao = useCallback((renovada: SessaoRenovada) => {
    setToken(renovada.token);
    setUser(renovada.user);
    setSessaoExpiraEm(null);
    concluirReautenticacao(renovada.token);
  }, []);

  useEffect(() => {
    let cancelado = false;
    let timerMe: number | undefined;

    const encerrar = () => {
      authService.logout();
      setUser(null);
      setToken(null);
    };

    // Token vencido ou recusado não é fim de sessão: a do Entra pode continuar viva. Renova e, se
    // nem o refresh token servir, tenta o redirect silencioso.
    const recuperar = async (tokenAtual: string, forcar: boolean): Promise<string | 'navegando' | null> => {
      const renovada = await renovarSessao({ forcar, tokenRecusado: forcar ? tokenAtual : undefined });
      if (renovada) {
        setToken(renovada.token);
        return renovada.token;
      }
      return (await retomarSessaoPeloEntra()) ? 'navegando' : null;
    };

    // Só 401 e 404 do /me encerram a sessão. Rede e 5xx são passageiros.
    const buscarMe = async (tk: string): Promise<ResultadoDoMe> => {
      try {
        const userData = (await authService.getMe(tk)) as AuthUser;
        if (!cancelado) {
          setUser(userData);
          setToken(tk);
          authService.setUser(userData);
        }
        return 'ok';
      } catch (e) {
        const status = (e as ApiError).status;
        if (status === 401) return 'recusado';
        if (status === 404) return 'removido';
        return 'indisponivel';
      }
    };

    // API fora do ar: a sessão continua com o usuário salvo e o /me é refeito em segundo plano
    // (5s, 10s, 20s, 40s, depois a cada minuto). Um 401 numa chamada da tela segue a política do
    // authService (renovação ou diálogo).
    const tentarMeDeNovo = (tentativa: number) => {
      timerMe = window.setTimeout(
        async () => {
          const tk = authService.getToken();
          if (cancelado || !tk) return;
          const resultado = await buscarMe(tk);
          if (resultado === 'indisponivel') tentarMeDeNovo(tentativa + 1);
          else if (resultado === 'removido') encerrar();
        },
        Math.min(5_000 * 2 ** tentativa, 60_000)
      );
    };

    const verifyAuth = async () => {
      let savedToken = authService.getToken();
      if (!savedToken) {
        setLoading(false);
        return;
      }
      if (tokenVencido(savedToken)) {
        const recuperado = await recuperar(savedToken, false);
        if (recuperado === 'navegando') return; // o bootstrap conclui na volta do Entra
        if (!recuperado) {
          encerrar();
          setLoading(false);
          return;
        }
        savedToken = recuperado;
      }

      let resultado = await buscarMe(savedToken);
      // O servidor recusou um token que o relógio daqui dava como válido: a mesma saída, forçando.
      if (resultado === 'recusado') {
        const recuperado = await recuperar(savedToken, true);
        if (recuperado === 'navegando') return;
        resultado = recuperado ? await buscarMe(recuperado) : 'recusado';
      }

      if (resultado === 'recusado' || resultado === 'removido') encerrar();
      else if (resultado === 'indisponivel') {
        // Sem o usuário salvo não há o que mostrar até a API voltar.
        if (authService.getStoredUser()) tentarMeDeNovo(0);
        else encerrar();
      }
      if (!cancelado) setLoading(false);
    };

    verifyAuth();
    return () => {
      cancelado = true;
      window.clearTimeout(timerMe);
    };
  }, []);

  // Renova o JWT antes de vencer: no horário e sempre que a aba volta a ficar visível, porque timer
  // de aba em segundo plano atrasa e não corre com o computador suspenso.
  useEffect(() => {
    if (loading || !token) return;
    let cancelado = false;
    let timer: number | undefined;

    function agendar(atrasoMs: number) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void tentar(false), atrasoMs);
    }

    async function tentar(voltouParaAba: boolean) {
      if (!precisaRenovar(authService.getToken())) return;
      const renovada = await renovarSessao();
      if (cancelado) return;
      if (renovada) {
        aplicarSessao(renovada);
        return;
      }
      if (!tokenVencido(authService.getToken())) {
        // Ainda vale, mas não renovou sozinho (no Safari o iframe não alcança a sessão do Entra):
        // avisa a hora do fim e oferece o popup, e segue tentando no silêncio.
        if (podeRenovarPeloEntra()) setSessaoExpiraEm(expiracaoDoToken(authService.getToken()));
        agendar(INTERVALO_NOVA_TENTATIVA_MS);
        return;
      }
      // Venceu e o refresh token não serve: só redireciona quando a pessoa acabou de voltar para a
      // aba. No meio do uso, um redirect inesperado levaria o que está sendo digitado.
      if (voltouParaAba && document.visibilityState === 'visible') await retomarSessaoPeloEntra();
    }

    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void tentar(true);
    };

    const exp = expiracaoDoToken(token);
    if (exp !== null) agendar(Math.max(exp - ANTECEDENCIA_RENOVACAO_MS - Date.now(), 0));
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    window.addEventListener('online', aoVoltar);
    return () => {
      cancelado = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      window.removeEventListener('online', aoVoltar);
    };
  }, [loading, token, aplicarSessao]);

  // As abas compartilham o localStorage. Outra aba renovou ou entrou de novo: esta adota o token,
  // e o diálogo de sessão encerrada daqui (se aberto) se resolve. Outra aba saiu: esta vai para o
  // login também, sem reload.
  useEffect(() => {
    const aoMudarStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === null) {
        const atual = authService.getToken();
        if (atual) {
          setToken(atual);
          setSessaoExpiraEm(null);
          concluirReautenticacao(atual);
        } else {
          setToken(null);
          setUser(null);
          setSessaoExpiraEm(null);
          concluirReautenticacao(null);
        }
      }
      if (e.key === USER_KEY && e.newValue) setUser(authService.getStoredUser());
    };
    window.addEventListener('storage', aoMudarStorage);
    return () => window.removeEventListener('storage', aoMudarStorage);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const data = (await authService.login(email, password)) as LoginResult;
    authService.setToken(data.token);
    authService.setUser(data.user);
    desmarcarSessaoViaEntra();
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const loginEntra = useCallback(async (idToken: string): Promise<LoginResult> => {
    const data = (await authService.ssoExchange(idToken)) as LoginResult;
    authService.setToken(data.token);
    authService.setUser(data.user);
    marcarSessaoViaEntra(data.user.email);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  // Sair encerra também o diálogo de sessão encerrada: as chamadas que esperavam nele falham, e o
  // AuthGuard leva ao login guardando a rota.
  const logout = useCallback(() => {
    authService.logout();
    setToken(null);
    setUser(null);
    setSessaoExpiraEm(null);
    concluirReautenticacao(null);
  }, []);

  const continuarConectado = useCallback(async () => {
    aplicarSessao(await renovarSessaoComPopup());
  }, [aplicarSessao]);

  const reentrarComSenha = useCallback(
    async (senha: string) => {
      aplicarSessao(await retomarComSenha(senha));
    },
    [aplicarSessao]
  );

  // Re-busca o /me e atualiza o estado/local storage (ex.: após trocar avatar/nome no perfil).
  const refreshUser = useCallback(async () => {
    const savedToken = authService.getToken();
    if (!savedToken) return;
    try {
      const userData = (await authService.getMe(savedToken)) as AuthUser;
      setUser(userData);
      authService.setUser(userData);
    } catch {
      /* mantém o usuário atual se a atualização falhar */
    }
  }, []);

  const permissions = user?.permissions || [];
  const hasPermission = useCallback(
    (perm: string) => {
      if (!user || !user.permissions) return false;
      return user.permissions.includes(perm);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        login,
        loginEntra,
        logout,
        refreshUser,
        permissions,
        hasPermission,
        sessaoExpiraEm,
        continuarConectado,
        sessaoEncerrada,
        reentrarComSenha
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
